import React, { useCallback } from 'react';
import {
  useDeleteAllNotificationsMutation,
  useGetNotificationQuery,
} from '@src/store/rtk/twilio';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import {
  ArrowRightIcon,
  CloseIcon,
  DeleteIcon,
  FilterLightIcon,
} from '@src/svg';
import {
  incrementPageNumber,
  resetNotifications,
  selectDisplayNotifications,
  selectEffectiveTotalElements,
  selectNotification,
  setIsActiveFilter,
} from '@store/slices/notification/notification';
import NotificationItem from '@component/NotificationDropdown/NotificationItem/NotificationItem';
import { logger } from '@utils/logger';
import { useDeleteUserNotificationMutation } from '@store/rtk/users';
import { EMPTY_STATE_MESSAGES } from '@utils/emptyStateMessages';
import { toastError } from '@utils/helpers';
import { selectUsers } from '@store/slices/users/usersSlice';
import { postNotificationSync } from '@utils/notificationSync';
import { toastMessage } from '@utils/toastMessage';

type Props = {
  isOpen: boolean;
  isMobile: boolean;
  onClose: () => void;
};

const NotificationDropdown = ({ isOpen, isMobile, onClose }: Props) => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector(selectUsers);
  const { pageNumber, pageSize, isActiveFilter } =
    useAppSelector(selectNotification);
  const displayContent = useAppSelector(selectDisplayNotifications);
  const effectiveTotalElements = useAppSelector(selectEffectiveTotalElements); // NEW

  const [deleteAllNotifications, { isLoading: isDeleting }] =
    useDeleteAllNotificationsMutation();
  const [deleteUserNotification, { isLoading: isUserBeingDeleted }] =
    useDeleteUserNotificationMutation();

  const isFilterDisabled = !displayContent.length && !isActiveFilter;
  const isDeleteDisabled =
    !displayContent.length || isDeleting || isUserBeingDeleted;

  const toggleFilter = () => {
    if (isFilterDisabled) return;
    dispatch(setIsActiveFilter(!isActiveFilter));
  };

  const { isFetching, refetch } = useGetNotificationQuery(
    {
      page: pageNumber,
      size: pageSize,
      ...(isActiveFilter ? { isRead: false } : {}),
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !isOpen && !isMobile,
    }
  );

  const handleShowMore = () => {
    if (displayContent.length < effectiveTotalElements && !isFetching) {
      dispatch(incrementPageNumber());
    }
  };

  const handleDeleteAll = async () => {
    if (isDeleteDisabled) return;

    const userNotificationId = displayContent.filter((el) => !el.rideId)?.[0]
      ?.id;
    try {
      await deleteAllNotifications().unwrap();
      if (userNotificationId) {
        await deleteUserNotification({ id: userNotificationId }).unwrap();
      }
      dispatch(resetNotifications());
      postNotificationSync({ type: 'delete_all', userId: userInfo?.id });
    } catch (e) {
      toastError(e, toastMessage.NOTIFICATION.failedToClearNotifications);
      logger.error(e);
    }
  };

  const renderNotifications = useCallback(() => {
    return displayContent.map((notification) => (
      <NotificationItem
        onClose={onClose}
        key={notification.id}
        notification={notification}
        refetch={refetch}
      />
    ));
  }, [displayContent, refetch]);

  if (!isMobile && !isOpen) return null;

  const hasMoreToLoad = displayContent.length < effectiveTotalElements;

  return (
    <div
      data-test-id="c-notification-dropdown"
      className={`notification-wrapper ${isMobile ? 'mobile-drawer' : ''} ${isOpen ? 'open' : ''}`}
    >
      <div className="notification-head" data-test-id="c-notification-head">
        <button className="notif-back" onClick={onClose} type="button">
          <ArrowRightIcon />
        </button>

        <h3>Notifications</h3>
        <div style={{ display: 'flex', gap: 10 }}>
          <div
            className="filter-toggle"
          >
            <button
              data-test-id="c-notification-filter-toggle"
              type="button"
              onClick={toggleFilter}
              disabled={isFilterDisabled}
              className={isFilterDisabled ? 'disabled' : ''}
            >
              {isActiveFilter ? (
                <CloseIcon width={24} height={24} />
              ) : (
                <FilterLightIcon width={24} height={24} />
              )}
            </button>
            <button
              data-test-id="c-notification-delete-all-button"
              type="button"
              onClick={handleDeleteAll}
              disabled={isDeleteDisabled}
            >
              <DeleteIcon width={24} height={24} />
            </button>
          </div>
        </div>
      </div>

      <div className="notification-body">
        {displayContent.length ? (
          <>
            <div className="notification-items">{renderNotifications()}</div>

            {hasMoreToLoad && (
              <span
                onClick={handleShowMore}
                className={`show_more ${isFetching ? 'disabled' : ''}`}
              >
                {isFetching ? 'Loading...' : 'Show More'}
              </span>
            )}
          </>
        ) : (
          <div
            className="empty_notifications"
            data-test-id="c-empty-notifications"
          >
            {isFetching
              ? 'Loading...'
              : EMPTY_STATE_MESSAGES.notificationsDropdown.empty}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationDropdown;
